/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import styled from 'styled-components';

import { ReactComponent as DreamStateClickUp } from '../../../static/images/logos/dream-state-clickup.svg';
import { ReactComponent as DreamStateModernTreasury } from '../../../static/images/logos/dream-state-modern-treasury.svg';
import { ReactComponent as DreamStateNotion } from '../../../static/images/logos/dream-state-notion.svg';
import { ReactComponent as DreamStateStubHub } from '../../../static/images/logos/dream-state-stubhub.svg';
import { GradientHeader, Text } from '../design-system';
import { AnchorLines } from '../design-system/AnchorLines';
import { ToutWrapper } from '../design-system/ToutWrapper';
import { GRID_SIZE } from '../global/PaperGrid';
import { SocialTout } from '../global/SocialTout';

import {
  CustomerStoryItem,
  type CustomerStoryItemType
} from './NewStandardForDataWorkItem';

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
  padding: 0 ${GRID_SIZE}px;
  z-index: 1;
  position: relative;
`;

const Top = styled.div`
  display: flex;
  border-top: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  border-left: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  border-right: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  position: relative;
`;

const Grid = styled.div`
  display: flex;
  border: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  position: relative;
`;

const GridInner = styled.div`
  display: flex;

  @media (max-width: ${({ theme }) => theme.mediaQuery.EXTRA_LARGE}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`;

const GridBkg = styled.div`
  flex: none;
  position: relative;
  pointer-events: none;
  background-image: linear-gradient(
      ${({ theme }) => theme.borderColor.MUTED} 1px,
      transparent 1px
    ),
    linear-gradient(
      to right,
      ${({ theme }) => theme.borderColor.MUTED} 1px,
      transparent 1px
    );
  background-size: ${GRID_SIZE * 2}px ${GRID_SIZE * 2}px;
`;

const TallGrid = styled(GridBkg)`
  width: ${GRID_SIZE * 4}px;
  height: calc(${GRID_SIZE * 12}px - 1px);

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    width: ${GRID_SIZE * 2}px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: none;
  }
`;

const LeftGrid = styled(TallGrid)`
  background-position: -1px -1px;
`;

const RightGrid = styled(TallGrid)`
  background-position: 0 -1px;
`;

const WideGrid = styled(GridBkg)`
  width: 100%;
  height: ${GRID_SIZE * 2}px;
`;

const TopGrid = styled(WideGrid)`
  background-position: center -1px;
  margin-bottom: auto;
`;

const BottomGrid = styled(WideGrid)`
  background-position: center 0;
  margin-top: auto;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 30px;
  gap: 15px;
`;

const StyledHeading = styled(GradientHeader).attrs({
  $purple: true,
  renderAs: 'h1'
})`
  && {
    margin-bottom: 0;
    padding: 0;
    max-width: 600px;
  }
`;

const StyledText = styled(Text).attrs({ size: 'large' })`
  && {
    text-align: center;
  }
`;

const StyledSocialTout = styled(SocialTout)`
  && {
    padding-left: 0;
    padding-right: 0;
  }
`;

const CONTENT: CustomerStoryItemType[] = [
  {
    title: 'Rapid exploration on-demand',
    description:
      'StubHub realigned its analytics stack with Hex for speed and quality.',
    link: '/customers/stubhub/',
    logo: <DreamStateStubHub />
  },
  {
    title: 'Operationalized data science',
    description:
      'ClickUp used Hex to turn a data science churn project into a cross-functional, customer-saving program.',
    link: '/customers/clickup/',
    logo: <DreamStateClickUp />
  },
  {
    title: 'One place for insights',
    description:
      'Notion made Hex the one place for everyone, from data science to CX and sales, to make decisions with data.',
    link: '/customers/notion/',
    logo: <DreamStateNotion />
  },
  {
    title: 'Empowering exploration',
    description:
      'Modern Treasury uses Hex to enable Engineering, Product, and Design teams to explore on their own.',
    link: '/customers/modern-treasury/',
    logo: <DreamStateModernTreasury />
  }
];

export const CustomerStoriesTout: React.ComponentType = React.memo(() => {
  return (
    <ToutWrapper>
      <Inner>
        <Top>
          <AnchorLines position="top-left" />
          <AnchorLines position="top-right" />
          <AnchorLines design="middle" position="bottom-left" />
          <AnchorLines design="middle" position="bottom-right" />
          <LeftGrid />
          <Title>
            <TopGrid />
            <Heading>
              <StyledHeading renderAs="h1">
                A new standard for data work
              </StyledHeading>
              <StyledText>
                Hex helps 1,000s of teams do more with their data.
              </StyledText>
            </Heading>
            <BottomGrid />
          </Title>
          <RightGrid />
        </Top>
        <Grid>
          <AnchorLines position="bottom-left" />
          <AnchorLines position="bottom-right" />
          <GridInner>
            {CONTENT.map((item, index) => (
              <CustomerStoryItem key={index} {...item} />
            ))}
          </GridInner>
        </Grid>
        <StyledSocialTout
          customerQuotes={[
            {
              author: 'Abhishek Modi',
              authorAvatarPath:
                'https://images.ctfassets.net/mmgv7yhaaeds/5RiuLohiQMsh4b1JS12ZnY/5d04efeba4e1fbc5e491ac0735a89119/abhishek-modi-headshot.jpg',
              authorTitle: 'Software Engineer',
              customerName: 'Notion',
              logoPath:
                'https://images.ctfassets.net/mmgv7yhaaeds/45K7YZyXLDrOeeYIN7Vi3y/d20ebb5f53a1f1bf6e5a061d828f2b09/Notion.svg',
              quote:
                'Our vision for Notion’s data team is that anyone, regardless of technical proficiency, is comfortable using data to answer their own questions — and Hex enables that.'
            }
          ]}
          includeG2Proof={true}
        />
      </Inner>
    </ToutWrapper>
  );
});
